import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { KeycloakContext } from '../App';
import { CircleStackIcon, EyeIcon, EyeSlashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate, formatDateTime } from '../lib/format';
import { Button } from '../components/ui/button';

interface DatabaseOffer {
  code: string;
  name: string;
  engine: string;
  storage_gb: number;
  price_ht: number;
  price_ttc: number;
}

interface DatabaseItem {
  id: string;
  offer_code?: string;
  offer_name?: string;
  engine?: string;
  storage_gb?: number;
  price_ht?: number;
  price_ttc?: number;
  // MySQL
  ip_address?: string;
  port?: number;
  username?: string;
  password?: string;
  database_name?: string;
  // MongoDB
  connection_string?: string;
  cluster?: string;
  // Access
  client_url?: string;
  // Free-text user/admin label (e.g. "DB001")
  label?: string;
  created_at?: string;
}

const TVA_RATE = 0.2;

const ENGINE_LABELS: Record<string, string> = {
  mongodb: 'MongoDB',
  mysql: 'MySQL',
};

const STORAGE_LABELS: Record<string, string> = {
  'mongodb-512mb': '512 MB',
  'mongodb-5gb': '5 GB',
  'mongodb-10gb': '10 GB',
  'mysql-512mb': '512 MB',
  'mysql-5gb': '5 GB',
  'mysql-10gb': '10 GB',
};

export default function Databases() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [databases, setDatabases] = useState<DatabaseItem[]>([]);
  const [offers, setOffers] = useState<DatabaseOffer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Order wizard
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [orderStep, setOrderStep] = useState<number>(0);
  const [selectedEngine, setSelectedEngine] = useState<string>('');
  const [selectedOfferCode, setSelectedOfferCode] = useState<string>('');
  const [orderSubmitting, setOrderSubmitting] = useState<boolean>(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // Details modal
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [selectedDb, setSelectedDb] = useState<DatabaseItem | null>(null);
  const [showCredentials, setShowCredentials] = useState<boolean>(false);
  const [labelInput, setLabelInput] = useState<string>('');
  const [savingLabel, setSavingLabel] = useState<boolean>(false);

  const openDetails = (db: DatabaseItem) => {
    setSelectedDb(db);
    setShowCredentials(false);
    setLabelInput(db.label || '');
    setShowDetailsModal(true);
  };

  const handleSaveLabel = async () => {
    if (!selectedDb) return;
    const trimmed = labelInput.trim();
    if ((selectedDb.label || '') === trimmed) return;
    setSavingLabel(true);
    try {
      const res = await fetch(`${API_URL}/databases/${selectedDb.id}/label`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: trimmed }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to update label');
      }
      const updated: DatabaseItem = await res.json();
      setDatabases((prev) => prev.map((d) => (d.id === selectedDb.id ? { ...d, label: updated.label } : d)));
      setSelectedDb({ ...selectedDb, label: updated.label });
      toast('Label updated', 'success');
    } catch (err: any) {
      toast(err.message || 'Failed to update label', 'error');
    } finally {
      setSavingLabel(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  // Auto-open detail modal when ?databaseId= is in the URL (e.g., navigated from Services page)
  useEffect(() => {
    const databaseId = searchParams.get('databaseId');
    if (databaseId && databases.length > 0) {
      const db = databases.find((d) => String(d.id) === databaseId);
      if (db) {
        openDetails(db);
        setSearchParams({}, { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databases, searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dbRes, offersRes] = await Promise.all([
        fetch(`${API_URL}/databases`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/databases/offers`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      ]);
      if (!dbRes.ok) throw new Error('Failed to fetch databases');
      if (!offersRes.ok) throw new Error('Failed to fetch offers');
      const [dbData, offersData] = await Promise.all([dbRes.json(), offersRes.json()]);
      setDatabases(dbData || []);
      setOffers(offersData || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAccessUrl = (db: DatabaseItem): string => {
    if (db.engine === 'mongodb' && db.client_url && db.username && db.password) {
      try {
        const url = new URL(db.client_url);
        url.username = db.username;
        url.password = db.password;
        return url.toString();
      } catch {
        return db.client_url;
      }
    }
    return db.client_url || '#';
  };

  const filteredOffers = offers.filter((o) => o.engine === selectedEngine);
  const selectedOffer = offers.find((o) => o.code === selectedOfferCode);

  const formatPrice = (val: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);

  const openOrderModal = () => {
    setOrderStep(0);
    setSelectedEngine('');
    setSelectedOfferCode('');
    setOrderError(null);
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
  };

  const canNextStep = () => {
    if (orderStep === 0) return !!selectedEngine;
    if (orderStep === 1) return !!selectedOfferCode;
    return true;
  };

  const handlePlaceOrder = async () => {
    if (!selectedOffer) return;
    setOrderError(null);
    setOrderSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/orders/database`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          offerCode: selectedOfferCode,
          engine: selectedEngine,
          storageGB: selectedOffer.storage_gb,
          priceHT: selectedOffer.price_ht,
          priceTTC: selectedOffer.price_ttc,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to place order');
      }
      toast('Order placed successfully!', 'success');
      closeOrderModal();
      await fetchData();
    } catch (err: any) {
      setOrderError(err.message);
    } finally {
      setOrderSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<CircleStackIcon />}
          title="Databases"
          description="Manage your managed databases."
          actions={
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm"
              disabled
            >
              Order new Database
            </button>
          }
        />
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  if (error)
    return (
      <div className="rounded-md bg-destructive/10 p-4">
        <p className="text-sm text-destructive">Error: {error}</p>
      </div>
    );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<CircleStackIcon />}
        title="Databases"
        description="Manage your managed databases."
        actions={
          <button
            type="button"
            onClick={openOrderModal}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus-visible:ring-ring focus:ring-offset-2 cursor-pointer"
          >
            Order new Database
          </button>
        }
      />

      {/* Order Wizard Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm" onClick={closeOrderModal} />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-foreground">New Database Order</h2>
                  <button type="button" onClick={closeOrderModal} className="text-muted-foreground hover:text-muted-foreground cursor-pointer">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Step indicator */}
                <div className="flex items-center mb-8">
                  {['Engine', 'Offer'].map((label, i) => (
                    <React.Fragment key={i}>
                      <div className={`flex items-center ${i <= orderStep ? 'text-primary' : 'text-muted-foreground'}`}>
                        <span
                          className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium border-2 ${
                            i < orderStep
                              ? 'bg-primary border-primary text-white'
                              : i === orderStep
                              ? 'border-primary text-primary'
                              : 'border-border text-muted-foreground'
                          }`}
                        >
                          {i < orderStep ? '✓' : i + 1}
                        </span>
                        <span className="ml-2 text-sm font-medium hidden sm:block">{label}</span>
                      </div>
                      {i < 1 && <div className={`flex-1 h-0.5 mx-4 ${i < orderStep ? 'bg-primary' : 'bg-muted'}`} />}
                    </React.Fragment>
                  ))}
                </div>

                {/* Step 0: Choose Engine */}
                {orderStep === 0 && (
                  <div className="space-y-4">
                    <h3 className="text-md font-medium text-foreground">Choose a database engine</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {['mongodb', 'mysql'].map((engine) => (
                        <div
                          key={engine}
                          onClick={() => setSelectedEngine(engine)}
                          className={`cursor-pointer rounded-lg border-2 p-6 text-center transition-all ${
                            selectedEngine === engine
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-border'
                          }`}
                        >
                          <div className="text-2xl font-bold text-foreground mb-1">
                            {engine === 'mongodb' ? 'MongoDB' : 'MySQL'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {engine === 'mongodb' ? 'NoSQL document database' : 'Relational SQL database'}
                          </div>
                          {selectedEngine === engine && (
                            <div className="mt-3 text-primary">
                              <svg className="h-5 w-5 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 1: Choose Offer */}
                {orderStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-md font-medium text-foreground">
                      Choose an offer for {selectedEngine === 'mongodb' ? 'MongoDB' : 'MySQL'}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {filteredOffers.map((offer) => (
                        <div
                          key={offer.code}
                          onClick={() => setSelectedOfferCode(offer.code)}
                          className={`cursor-pointer rounded-lg border-2 p-4 transition-all ${
                            selectedOfferCode === offer.code
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-border'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <span className="text-sm font-semibold text-foreground">
                              {offer.storage_gb === 0 ? '512 MB' : `${offer.storage_gb} GB`} Storage
                            </span>
                            {selectedOfferCode === offer.code && (
                              <span className="text-primary">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </span>
                            )}
                          </div>
                          <div className="text-primary font-semibold text-sm">
                            {formatPrice(offer.price_ht)} excl. VAT / month
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">{formatPrice(offer.price_ttc)} inc. VAT / month</div>
                        </div>
                      ))}
                    </div>

                    {selectedOffer && (
                      <div className="bg-muted/30 border border-border rounded-lg p-4 mt-4">
                        <h4 className="text-sm font-medium text-foreground mb-2">Price Summary</h4>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <dt className="text-muted-foreground">{selectedOffer.name}</dt>
                            <dd className="font-medium">{formatPrice(selectedOffer.price_ht)}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-muted-foreground">VAT (20%)</dt>
                            <dd className="font-medium">{formatPrice(selectedOffer.price_ht * TVA_RATE)}</dd>
                          </div>
                          <div className="border-t border-border pt-1 flex justify-between text-primary font-bold">
                            <dt>Total inc. VAT / month</dt>
                            <dd>{formatPrice(selectedOffer.price_ttc)}</dd>
                          </div>
                        </dl>
                      </div>
                    )}
                  </div>
                )}

                {orderError && (
                  <div className="mt-4 rounded-md bg-destructive/10 p-3">
                    <p className="text-sm text-red-700">{orderError}</p>
                  </div>
                )}

                <div className="mt-6 flex justify-between items-center">
                  <button
                    type="button"
                    disabled={orderStep === 0}
                    onClick={() => setOrderStep((p) => p - 1)}
                    className="px-4 py-2 border border-border text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/40 disabled:opacity-40 cursor-pointer"
                  >
                    Back
                  </button>
                  {orderStep < 1 ? (
                    <button
                      type="button"
                      disabled={!canNextStep()}
                      onClick={() => setOrderStep((p) => p + 1)}
                      className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-40 cursor-pointer"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={!canNextStep() || orderSubmitting}
                      onClick={handlePlaceOrder}
                      className="px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
                    >
                      {orderSubmitting ? 'Placing order...' : 'Place Order'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Database List */}
      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="w-full overflow-x-auto touch-pan-x">
        <table className="w-full min-w-[860px] divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Engine</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Label</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Storage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {databases.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-muted-foreground">
                  You do not have any database yet.
                </td>
              </tr>
            ) : (
              databases.map((db) => (
                <tr key={db.id} className="hover:bg-muted/40">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                    <span
                      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset bg-success/10 text-success ring-success/20"
                    >
                      {ENGINE_LABELS[db.engine || ''] || db.engine}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">{db.offer_name || db.offer_code}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                    {db.label ? db.label : <span className="text-muted-foreground italic">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {db.offer_code ? (STORAGE_LABELS[db.offer_code] || `${db.storage_gb} GB`) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {db.price_ht != null ? `${formatPrice(db.price_ht)} excl. VAT` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {db.created_at ? formatDate(db.created_at) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <div className="inline-flex items-center gap-2">
                      {db.client_url && (
                        <a
                          href={getAccessUrl(db)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                        >
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                          {db.engine === 'mongodb' ? 'Mongo Express' : 'phpMyAdmin'}
                        </a>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openDetails(db)}>Details</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Database Details Modal */}
      {showDetailsModal && selectedDb && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="db-modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity"
              aria-hidden="true"
              onClick={() => setShowDetailsModal(false)}
            />
            <div className="inline-block align-bottom bg-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-card px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground" id="db-modal-title">
                      {selectedDb.offer_name || selectedDb.offer_code}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {ENGINE_LABELS[selectedDb.engine || ''] || selectedDb.engine} · Database #{selectedDb.id}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDetailsModal(false)}
                    className="text-muted-foreground hover:text-muted-foreground cursor-pointer"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Label editor */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">Label</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        maxLength={128}
                        placeholder="e.g. DB001"
                        className="block w-full rounded-md border border-border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary"
                      />
                      <button
                        type="button"
                        onClick={handleSaveLabel}
                        disabled={savingLabel || (selectedDb.label || '') === labelInput.trim()}
                        className="inline-flex items-center rounded-md border border-transparent px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {savingLabel ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">A friendly name shown in your Services list. Leave empty to clear.</p>
                  </div>

                  {selectedDb.engine === 'mysql' ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">IP Address</p>
                          <p className="mt-1 text-sm text-foreground font-mono">{selectedDb.ip_address || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Port</p>
                          <p className="mt-1 text-sm text-foreground font-mono">{selectedDb.port || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Database</p>
                          <p className="mt-1 text-sm text-foreground font-mono">{selectedDb.database_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Username</p>
                          <p className="mt-1 text-sm text-foreground font-mono">{selectedDb.username || '-'}</p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Password</p>
                          <button
                            type="button"
                            onClick={() => setShowCredentials(!showCredentials)}
                            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 cursor-pointer"
                          >
                            {showCredentials ? (
                              <><EyeSlashIcon className="h-4 w-4" /> Hide</>
                            ) : (
                              <><EyeIcon className="h-4 w-4" /> Show</>
                            )}
                          </button>
                        </div>
                        <p className="mt-1 text-sm text-foreground font-mono break-all">
                          {showCredentials ? (selectedDb.password || '-') : '••••••••••••'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Username</p>
                        <p className="mt-1 text-sm text-foreground font-mono">{selectedDb.username || '-'}</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Password</p>
                          <button
                            type="button"
                            onClick={() => setShowCredentials(!showCredentials)}
                            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 cursor-pointer"
                          >
                            {showCredentials ? (
                              <><EyeSlashIcon className="h-4 w-4" /> Hide</>
                            ) : (
                              <><EyeIcon className="h-4 w-4" /> Show</>
                            )}
                          </button>
                        </div>
                        <p className="mt-1 text-sm text-foreground font-mono break-all">
                          {showCredentials ? (selectedDb.password || '-') : '••••••••••••'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Cluster</p>
                        <p className="mt-1 text-sm text-foreground font-mono">{selectedDb.cluster || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Connection String</p>
                        <p className="mt-1 text-sm text-foreground font-mono break-all">
                          {showCredentials ? (selectedDb.connection_string || '-') : (selectedDb.connection_string ? '••••••••••••' : '-')}
                        </p>
                      </div>
                    </>
                  )}

                  <div className="border-t border-border pt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Storage</p>
                      <p className="mt-1 text-foreground">
                        {selectedDb.offer_code ? (STORAGE_LABELS[selectedDb.offer_code] || `${selectedDb.storage_gb} GB`) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Price</p>
                      <p className="mt-1 text-foreground">
                        {selectedDb.price_ht != null ? `${formatPrice(selectedDb.price_ht)} excl. VAT / month` : '-'}
                      </p>
                    </div>
                    {selectedDb.created_at && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</p>
                        <p className="mt-1 text-foreground">{formatDateTime(selectedDb.created_at)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-between items-center px-4 pb-4 sm:px-6 sm:pb-6">
                {selectedDb.client_url ? (
                  <a
                    href={getAccessUrl(selectedDb)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 justify-center rounded-md border border-primary px-4 py-2 text-sm font-medium text-primary shadow-sm hover:bg-primary/10"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    {selectedDb.engine === 'mongodb' ? 'Mongo Express' : 'phpMyAdmin'}
                  </a>
                ) : <span />}
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  className="inline-flex justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted/40 cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
